/**
 * Выпадашка поиска «Чатов» (владелец 22.10.26): по фокусу в поле — переключатель «Чаты / Серия /
 * Сделка / Клиент / Договор» и три моих последних звонка, чтобы сразу вернуться к клиенту, с которым
 * только что говорили («3 мин назад»); клик — номер в поиск, как всплывашка входящего. В режиме
 * «Серия» поле поиска принимает номер серии, Enter открывает карточку серии; «Сделка», «Клиент»
 * (карточка клиента CRM), «Договор» — номер, Enter открывает чат клиента (`GET /api/chats/number`).
 * Звонки — тот же запрос, что вкладка «Звонки» (`GET /api/chats/calls`).
 * Спеки `docs/specs/2026-10-22-serial-models-design.md`, `docs/specs/2026-10-22-chat-numbers-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { type RefObject, useEffect } from "react";

import { loadMyCalls } from "@/lib/clientChats";

export type SearchMode = "chats" | "serial" | "deal" | "customer" | "contract";

/** Режимы с номером вместо текста: подпись кнопки, подсказка поля и что делает Enter. */
export const NUMBER_MODES: Record<Exclude<SearchMode, "chats">, { label: string; placeholder: string; hint: string }> = {
  serial: { label: "Серия", placeholder: "Номер серии", hint: "Введите номер серии и нажмите Enter — откроется карточка серии." },
  deal: { label: "Сделка", placeholder: "Номер сделки", hint: "Введите номер сделки и нажмите Enter — откроется чат клиента." },
  customer: {
    label: "Клиент",
    placeholder: "Номер клиента CRM",
    hint: "Введите номер клиента CRM и нажмите Enter — откроется чат клиента.",
  },
  contract: { label: "Договор", placeholder: "Номер договора", hint: "Введите номер договора и нажмите Enter — откроется чат клиента." },
};

const SHOWN = 3;

/** Время звонков — московское без пояса: «N мин назад» считаем от MSK, где бы ни сидел сотрудник. */
function ago(at: string) {
  const min = Math.max(0, Math.round((Date.now() - Date.parse(`${at.replace(" ", "T")}+03:00`)) / 60_000));
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} ч назад`;
  return `${at.slice(8, 10)}.${at.slice(5, 7)} ${at.slice(11, 16)}`;
}

export function SearchDrop({
  anchor,
  mode,
  setMode,
  note,
  onPhone,
  onClose,
}: {
  /** Поле с выпадашкой: клик вне его закрывает. */
  anchor: RefObject<HTMLElement | null>;
  mode: SearchMode;
  setMode: (m: SearchMode) => void;
  /** Итог Enter в режиме номера: «Ищу…», «не нашли», ошибка. */
  note?: string;
  onPhone: (phone: string) => void;
  onClose: () => void;
}) {
  // Тот же ключ, что у вкладки «Звонки»: кэш общий, сервер сам докачивает свежие звонки из Bitrix.
  const calls = useQuery({
    queryKey: ["chat-my-calls", "mine"],
    queryFn: () => loadMyCalls("mine"),
    enabled: mode === "chats",
    staleTime: 15_000,
  });
  useEffect(() => {
    const away = (e: MouseEvent) => anchor.current && !anchor.current.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [anchor, onClose]);
  const rows = (calls.data?.items ?? []).filter((i) => i.call && i.phone).slice(0, SHOWN);
  const seg = (on: boolean) => `flex-1 rounded-full px-1 py-1 text-[13px] ${on ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"}`;

  return (
    <div
      className="absolute inset-x-0 top-[calc(100%+6px)] z-40 flex flex-col gap-1 rounded-[16px] bg-[var(--tg-surface)] p-2 text-[var(--tg-text)] shadow-[0_4px_16px_rgba(0,0,0,.28)]"
      data-annot="выпадашка поиска"
    >
      <div className="flex rounded-full bg-[var(--tg-input)] p-0.5">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setMode("chats")} className={seg(mode === "chats")}>
          Чаты
        </button>
        {(Object.keys(NUMBER_MODES) as (keyof typeof NUMBER_MODES)[]).map((m) => (
          <button key={m} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setMode(m)} className={seg(mode === m)}>
            {NUMBER_MODES[m].label}
          </button>
        ))}
      </div>
      {mode !== "chats" ? (
        <div className="px-2 py-1.5 text-[13px] text-[var(--tg-sec)]">{note || NUMBER_MODES[mode].hint}</div>
      ) : (
        <>
          <div className="px-2 pt-1 text-[12px] text-[var(--tg-sec)]">Последние звонки</div>
          {calls.isPending ? (
            <div className="px-2 py-1.5 text-[13px] text-[var(--tg-sec)]">Загружаю…</div>
          ) : rows.length === 0 ? (
            <div className="px-2 py-1.5 text-[13px] text-[var(--tg-sec)]">Звонков за неделю нет.</div>
          ) : (
            rows.map((i) => {
              const c = i.call!;
              const In = c.direction === "in" ? PhoneIncoming : PhoneOutgoing;
              const what = i.deal ? `Сделка ${i.deal.id}` : i.lead ? `Лид ${i.lead.id}` : "";
              return (
                <button
                  key={i.key}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPhone(i.phone!)}
                  className="flex items-center gap-2.5 rounded-[12px] px-2 py-1.5 text-left hover:bg-[var(--tg-hover)]"
                  title={c.direction === "in" ? "Входящий" : "Исходящий"}
                >
                  <In size={16} className={c.result === "talk" ? "text-[var(--tg-primary)]" : "text-red-500"} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px]">{i.name || i.phone}</span>
                    <span className="block truncate text-[12px] text-[var(--tg-sec)]">
                      {[ago(c.at), i.name ? i.phone : "", what].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </>
      )}
    </div>
  );
}
